import { matchPath, useLocation, useHistory, Prompt, Switch, Route } from 'react-router-dom';
export * from 'react-router-dom';
import { getAPM, APMTransaction } from '@interhyp/platform-apm';
import { Component, Suspense, useMemo, createContext, useState, useEffect, useContext as useContext$1, useCallback } from 'react';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { Spinner, Notification, Modal, ModalContent, ModalFooter } from '@platform/elements';
import { matchPath as matchPath$1, useHistory as useHistory$1 } from 'react-router';
import { createExtensionPoint } from '@interhyp/platform-plugin';

class ErrorBoundary extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      error: null
    };
  }
  static getDerivedStateFromError(error) {
    return {
      error
    };
  }
  componentDidUpdate(prevProps, prevState) {
    const isDifferentError = prevState.error !== this.state.error;
    if (this.state.error && isDifferentError) {
      const apm = getAPM();
      const error = Object.assign(this.state.error, {
        module: this.props.path
      });
      apm.captureError(error);
    }
  }
  render() {
    const {
      error
    } = this.state;
    const {
      children
    } = this.props;
    if (error !== null) {
      const {
        fallback
      } = this.props;
      if (fallback) {
        const Fallback = fallback;
        return /*#__PURE__*/jsx(Fallback, {
          error: error
        });
      }
      return null;
    }
    return /*#__PURE__*/jsx(Fragment, {
      children: children
    });
  }
}

function PlatformRoute(route) {
  const Component = route.component;
  return /*#__PURE__*/jsx(ErrorBoundary, {
    path: route.path,
    fallback: route.fallback,
    children: /*#__PURE__*/jsx(APMTransaction, {
      name: route.path,
      type: "route",
      children: /*#__PURE__*/jsx(Suspense, {
        fallback: null,
        children: /*#__PURE__*/jsx(Component, {})
      })
    })
  });
}

let RouteContext = /*#__PURE__*/function (RouteContext) {
  RouteContext["MORTGAGE"] = "mortgages";
  RouteContext["CONSUMER_CREDIT"] = "consumer-credits";
  RouteContext["SUBMISSION"] = "submissions";
  RouteContext["INSURANCE"] = "insurance";
  /**
   * @description: context for public routes without login e.g. calculators
   */
  RouteContext["PUBLIC"] = "public";
  return RouteContext;
}({});

function buildPath(config) {
  if (config.context === RouteContext.PUBLIC) {
    // Public context has no separate context-id because it's not required there
    return `/${config.context}${config.path}`;
  }
  if (config.context) {
    return `/${config.context}/:contextId${config.path}`;
  }
  return config.path;
}

const CONTEXT_SLUGS = Object.values(RouteContext);

/**
 * Extract context information from given path
 * @param pathname
 */
function useContextInformation(pathname) {
  const {
    contextId,
    contextSlug
  } = useMemo(() => {
    for (const contextSlug of CONTEXT_SLUGS) {
      const match = matchPath(pathname, {
        path: buildPath({
          path: '',
          context: contextSlug
        })
      });
      if (match) {
        const {
          contextId
        } = match.params;
        return {
          contextId,
          contextSlug
        };
      }
    }
    return {
      contextId: undefined,
      contextSlug: undefined
    };
  }, [pathname]);
  return useMemo(() => {
    if (contextId && contextSlug) {
      return {
        type: contextSlug,
        prefix: `/${contextSlug}/${contextId}`,
        id: contextId
      };
    }
  }, [contextId, contextSlug]);
}

const PlatformRouterContext = /*#__PURE__*/createContext(undefined);
function PlatformRouterProvider({
  children,
  pathname
}) {
  const value = useContextInformation(pathname);
  return /*#__PURE__*/jsx(PlatformRouterContext.Provider, {
    value: value,
    children: children
  });
}

function EmptyWrapper({
  children
}) {
  return /*#__PURE__*/jsx(Fragment, {
    children: children
  });
}

function hasIsolatedFlag(search) {
  const qs = new URLSearchParams(search);
  return qs.has('isolated');
}
function useIsIsolated() {
  const {
    search
  } = useLocation();
  const [isIsolated, setIsolated] = useState(hasIsolatedFlag(search));
  useEffect(() => {
    if (isIsolated) {
      setIsolated(true);
    }
  }, [isIsolated, search]);
  return isIsolated;
}

const missingBlockerFn = b => {
  console.error('No blocker provider defined!');
};
const missingRegisterBlockerFn = b => {
  missingBlockerFn();
  return b;
};
const routeBlockerContext = /*#__PURE__*/createContext({
  registerBlocker: missingRegisterBlockerFn,
  unregisterBlocker: missingBlockerFn,
  activeBlocker: undefined,
  setActiveBlocker: missingBlockerFn
});

var styles = {"loading":"loading_5U5RJqJhqv"};

const DEFAULT_MESSAGE = 'Sie haben Änderungen auf dieser Seite vorgenommen. Möchten Sie diese vor dem Verlassen der Seite speichern?';
const DEFAULT_MESSAGE_WITHOUT_SAVE = 'Sie haben Änderungen auf dieser Seite vorgenommen. Sind Sie sicher, dass Sie die Seite verlassen möchten?';
function RouteBlockerModalContent({
  saveStatus,
  hasSaveMethod,
  errorMessage,
  message
}) {
  if (saveStatus === 'LOADING') {
    return /*#__PURE__*/jsx("div", {
      className: styles.loading,
      children: /*#__PURE__*/jsx(Spinner, {})
    });
  }
  if (saveStatus === 'ERROR') {
    return /*#__PURE__*/jsx(Notification, {
      type: "error",
      children: errorMessage ?? 'Beim Speichern ihrer Änderungen ist ein Fehler aufgetreten'
    });
  }
  if (message) {
    return /*#__PURE__*/jsx(Fragment, {
      children: message
    });
  }
  if (hasSaveMethod) {
    return /*#__PURE__*/jsx(Fragment, {
      children: DEFAULT_MESSAGE
    });
  }
  return /*#__PURE__*/jsx(Fragment, {
    children: DEFAULT_MESSAGE_WITHOUT_SAVE
  });
}

function RouteBlockerModal({
  onProceed
}) {
  const {
    activeBlocker,
    setActiveBlocker,
    unregisterBlocker
  } = useContext$1(routeBlockerContext);
  const [saveStatus, setSaveStatus] = useState('IDLE');
  const [saveErrorMessage, setSaveErrorMessage] = useState();
  const isModalOpen = !!activeBlocker;
  const hasSaveMethod = !!activeBlocker?.onSave;
  const isLoading = saveStatus === 'LOADING';
  const closeModal = useCallback(() => {
    activeBlocker?.onClose?.();
    setActiveBlocker(undefined);
    setSaveStatus('IDLE');
  }, [activeBlocker, setActiveBlocker]);

  /**
   * The function calls the save method (if existing), closes the modal and proceed with the routing
   */
  const handleConfirmNavigationClick = useCallback(async shouldSave => {
    if (!activeBlocker) {
      return;
    }
    if (shouldSave && activeBlocker.onSave) {
      setSaveStatus('LOADING');
      try {
        await activeBlocker.onSave();
      } catch (error) {
        setSaveStatus('ERROR');
        if (error instanceof Error) {
          setSaveErrorMessage(error.message);
        }
        return;
      }
    }
    activeBlocker.onProceed?.();
    unregisterBlocker(activeBlocker);
    closeModal();
    onProceed?.();
  }, [activeBlocker, closeModal, unregisterBlocker, onProceed]);
  const actions = useMemo(() => {
    const actionlist = [{
      semantic: 'CANCEL',
      handler: closeModal,
      disabled: isLoading
    }, {
      semantic: hasSaveMethod ? 'SECONDARY' : 'PRIMARY',
      handler: () => handleConfirmNavigationClick(false),
      label: hasSaveMethod ? 'Nicht speichern' : 'Verlassen',
      disabled: isLoading
    }];
    if (hasSaveMethod) {
      actionlist.push({
        semantic: 'PRIMARY',
        handler: () => handleConfirmNavigationClick(true),
        label: 'Speichern',
        loading: isLoading,
        disabled: saveStatus === 'ERROR'
      });
    }
    return actionlist;
  }, [closeModal, handleConfirmNavigationClick, isLoading, hasSaveMethod, saveStatus]);
  return /*#__PURE__*/jsxs(Modal, {
    title: hasSaveMethod ? 'Änderungen speichern?' : 'Seite verlassen?',
    id: "RouteBlocker",
    open: isModalOpen,
    onClose: closeModal,
    size: "S",
    close: closeModal,
    closeable: true,
    closeOnEsc: true,
    children: [/*#__PURE__*/jsx(ModalContent, {
      children: /*#__PURE__*/jsx(RouteBlockerModalContent, {
        saveStatus: saveStatus,
        hasSaveMethod: hasSaveMethod,
        errorMessage: saveErrorMessage,
        message: activeBlocker?.message
      })
    }), /*#__PURE__*/jsx(ModalFooter, {
      actions: actions
    })]
  });
}

/**
 * Do not block the route if the incoming path match with the path of the blocker
 * @param incomingPath
 * @param pathDef
 */
function shouldBlockRoute(incomingPath, pathDef) {
  return !matchPath$1(incomingPath, pathDef);
}

/**
 * Block unload event which leads the user to a different page
 * @param blockUnload
 */
function useBlockUnload(blockUnload) {
  useEffect(() => {
    const unload = event => {
      if (blockUnload) {
        event.returnValue = true;
      }
    };
    window.addEventListener('beforeunload', unload);
    return () => window.removeEventListener('beforeunload', unload);
  }, [blockUnload]);
}

function RouteBlockerProvider({
  children
}) {
  const {
    push
  } = useHistory();
  const {
    pathname
  } = useLocation();
  const [activeBlocker, setActiveBlocker] = useState();
  const [blockedPath, setBlockedPath] = useState();
  const [shouldNavigateToBlockedPath, setShouldNavigateToBlockedPath] = useState(false);
  const [blockers, setBlockers] = useState([]);
  const registerBlocker = useCallback(newBlocker => {
    setBlockers(currentBlockers => [...currentBlockers, newBlocker]);
    return newBlocker;
  }, []);
  const unregisterBlocker = useCallback(removeBlocker => {
    setBlockers(currentBlockers => currentBlockers.filter(b => b !== removeBlocker));
  }, []);

  // Select the blocker by the lifo principle
  const latestBlocker = blockers[blockers.length - 1];

  /**
   * Set the activeBlocker depending on the shouldBlockRoute check
   */
  const handleRouteChange = useCallback(({
    pathname: incomingPath
  }) => {
    if (latestBlocker && shouldBlockRoute(incomingPath, latestBlocker.pathDefinition ?? {
      path: pathname,
      exact: true
    })) {
      // set the latest blocker active
      setActiveBlocker(latestBlocker);
      // save the incoming path as blocked path
      setBlockedPath(incomingPath);
      return false;
    }

    // if there is no blocker, just continue with the routing
    setActiveBlocker(undefined);
    return true;
  }, [latestBlocker, pathname]);

  /**
   * Force navigation
   */
  const proceedRouting = useCallback(() => {
    if (blockedPath) {
      setShouldNavigateToBlockedPath(true);
    }
  }, [blockedPath]);

  /**
   * Navigate to blocked path if shouldNavigateToBlockedPath is true and there is no active blocker
   */
  useEffect(() => {
    if (blockedPath && shouldNavigateToBlockedPath && !activeBlocker) {
      setShouldNavigateToBlockedPath(false);
      push(blockedPath);
    }
  }, [blockedPath, shouldNavigateToBlockedPath, activeBlocker, push]);
  useBlockUnload(!!latestBlocker);
  const value = useMemo(() => ({
    unregisterBlocker,
    registerBlocker,
    setActiveBlocker,
    activeBlocker
  }), [registerBlocker, unregisterBlocker, setActiveBlocker, activeBlocker]);
  return /*#__PURE__*/jsxs(routeBlockerContext.Provider, {
    value: value,
    children: [/*#__PURE__*/jsx(Prompt, {
      when: !!latestBlocker,
      message: handleRouteChange
    }), /*#__PURE__*/jsx(RouteBlockerModal, {
      onProceed: proceedRouting
    }), children]
  });
}

let FallbackType = /*#__PURE__*/function (FallbackType) {
  FallbackType[FallbackType["NOT_FOUND"] = 404] = "NOT_FOUND";
  FallbackType[FallbackType["UNAUTHORIZED"] = 401] = "UNAUTHORIZED";
  FallbackType[FallbackType["ERROR"] = 500] = "ERROR";
  return FallbackType;
}({});

function PlatformRouter({
  routes,
  viewport,
  fallback: Fallback,
  secure
}) {
  const Viewport = viewport ?? EmptyWrapper;
  const isIsolated = useIsIsolated();
  const location = useLocation();
  if (!secure) {
    return /*#__PURE__*/jsx(Fallback, {
      type: FallbackType.UNAUTHORIZED
    });
  }

  // type cast because react-router-dom v5 doesn't match with react v18
  const SwitchLegacy = Switch;
  const RouteLegacy = Route;
  return /*#__PURE__*/jsx(PlatformRouterProvider, {
    pathname: location.pathname,
    children: /*#__PURE__*/jsx(RouteBlockerProvider, {
      children: /*#__PURE__*/jsx(Viewport, {
        isolated: isIsolated,
        children: /*#__PURE__*/jsxs(SwitchLegacy, {
          children: [routes.map(route => {
            const contextPath = buildPath(route);
            return /*#__PURE__*/jsx(RouteLegacy, {
              exact: route.exact,
              path: contextPath,
              children: /*#__PURE__*/jsx(PlatformRoute, {
                ...route
              })
            }, contextPath);
          }), /*#__PURE__*/jsx(RouteLegacy, {
            children: /*#__PURE__*/jsx(Fallback, {
              type: FallbackType.NOT_FOUND
            })
          })]
        })
      })
    })
  });
}

function SaveLocation({
  children
}) {
  const location = useLocation();
  useEffect(() => {
    sessionStorage.setItem('targetLocation', JSON.stringify(location));
  }, [location]);
  return /*#__PURE__*/jsx(Fragment, {
    children: children
  });
}

const routeExtension = createExtensionPoint(Symbol('router'), true);

function useContext() {
  const {
    pathname
  } = useLocation();
  const contextInformation = useContextInformation(pathname);
  const providerContext = useContext$1(PlatformRouterContext);
  return providerContext ?? contextInformation;
}
const useBusinessContext = useContext;

function useContextRoutes(context) {
  const routes = routeExtension.useExtensions();
  return useMemo(() => {
    return routes.filter(route => route.context === context);
  }, [context, routes]);
}

function useFilteredRoutes(definedRoutes, context) {
  const routes = useContextRoutes(context);
  return useMemo(() => {
    return definedRoutes.filter(route => routes.find(availableRoute => matchPath$1(route.path, {
      path: availableRoute.path,
      exact: availableRoute.exact
    })));
  }, [definedRoutes, routes]);
}

function useSavedLocation(visit) {
  const {
    replace
  } = useHistory$1();
  useEffect(() => {
    if (visit) {
      const storedLocation = sessionStorage.getItem('targetLocation');
      if (storedLocation) {
        const location = JSON.parse(storedLocation);
        sessionStorage.removeItem('targetLocation');
        replace(location);
      }
    }
  }, [replace, visit]);
}

/**
 * Check if every key is present in the given path
 * @param path
 * @param schema
 */
function validateCustomContextExtension(path, schema) {
  return Object.keys(schema.shape).every(shapeKey => {
    return path.includes(`/:${shapeKey}/`) || path.endsWith(`/:${shapeKey}`);
  });
}

const internalExtensionPoint = createExtensionPoint(Symbol('custom-context'), true);
const customContextExtension = {
  ...internalExtensionPoint,
  createExtension: params => {
    if (!validateCustomContextExtension(params.path, params.context.schema)) {
      throw new Error('Invalid customContextExtension. Please check if all parameters defined in the schema are present in the path!');
    }
    return internalExtensionPoint.createExtension(params);
  }
};

const useCustomContext = () => {
  const contextDefinitions = customContextExtension.useExtensions();
  const {
    pathname
  } = useLocation();
  return useMemo(() => {
    for (const definition of contextDefinitions) {
      const matchingResult = matchPath(pathname, {
        path: definition.path,
        exact: false
      });
      if (matchingResult) {
        return {
          context: definition.context,
          params: matchingResult.params
        };
      }
    }
    return undefined;
  }, [contextDefinitions, pathname]);
};

/**
 * Activate the blocker depending on the given flag
 * @param activateBlockOnLeave
 * @param blockerConfig - see RouteBlocker config for details
 * @param dependencyList - dependency list when the blocker config updates (fallback is the blocker config itself)
 */
function useRouteBlocker(activateBlockOnLeave, blockerConfig, dependencyList = [blockerConfig]) {
  const {
    registerBlocker,
    unregisterBlocker
  } = useContext$1(routeBlockerContext);
  useEffect(() => {
    if (activateBlockOnLeave) {
      // The simplest blocker can be an empty object because all parameters are optional with default values
      const blocker = registerBlocker(blockerConfig ?? {});
      return () => unregisterBlocker(blocker);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activateBlockOnLeave, registerBlocker, unregisterBlocker, ...dependencyList]);
}

let RouteType = /*#__PURE__*/function (RouteType) {
  RouteType["PUBLIC"] = "public";
  RouteType["PRIVATE"] = "private";
  RouteType["SHARED"] = "shared";
  return RouteType;
}({});

const createCustomContext = (id, schema) => {
  const newContext = {
    id: Symbol(id),
    useContextParams: () => {
      const data = useCustomContext();
      if (data?.context === newContext) {
        return data.params;
      }
      return undefined;
    },
    schema
  };
  return newContext;
};

export { ErrorBoundary, FallbackType, PlatformRoute, PlatformRouter, PlatformRouterContext, PlatformRouterProvider, RouteBlockerProvider, RouteContext, RouteType, SaveLocation, createCustomContext, customContextExtension, routeBlockerContext, routeExtension, useBusinessContext, useContext, useContextInformation, useContextRoutes, useCustomContext, useFilteredRoutes, useRouteBlocker, useSavedLocation };
//# sourceMappingURL=index.js.map
